import Konva from 'konva'
import qc from 'qc'
import {
  isAudio,
  isDragTargetArea,
  isLangImage,
  isNodeGhost,
  isTempNode,
  isVideo,
  isDrawLineNode,
  isDrawLineAreaNode,
  isRestrictedNode,
  isDragBound,
  isEraseBrushCategory,
  hasLayoutComponentParent,
  isCarouselNodeChild,
  isCarouselResourceGroupNode,
  isCarouselPiteGroupNode,
  getAllParentsBeforeScene,
  isGroup,
  isIvHotAreaNode,
  isIvEndingInstallNode,
  isIvGroup,
} from '@ps/bridge/qc/base'
import store from '@ps/store'
import nodePool from '@ps/bridge/qc/node-pool'
import StageData from '@ps/store/workbench/stage/stage-data'
import { isDragonBoneChild } from '@ps/utils/game'
import { getNodeLvl } from '@ps/bridge/qc/node/utils'
import { getNodeByShape } from '@ps/store/workbench/stage/utils/helper'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import { GeneralMode } from '@ps/types/workbench/stage.type'
import { hasBrushImages } from '@ps/workbench/undo-redo/module/erase'
import { isFollowElementNode } from '@ps/workbench/undo-redo/module/mask'
import { isIvPage } from '@ps/router/config'
import { getBrainItemActionIndexByNodeUuid } from '@ps/store/interactive-video/brain/brain'

export const SpecName = 'psLayer'
export const SpecNodeGhostName = 'specNodeGhostName'
export const SpecSuffixName = 'specSuffixName'

export const disableDraggable = (node: qc.Node): boolean => {
  return isDragonBoneChild(node) || !node.worldVisible
}

export const SelectedNodeMoveToTop = false

// 处于编辑连线区域状态时,可视节点
export const allowShowNodeInDrawLineMode = (node: qc.Node): boolean => {
  const drawLineMode = store.state.workspace.stage.mode.drawLine
  const drawLineAreaMode = store.state.workspace.stage.mode.drawLineArea
  if (
    drawLineMode !== GeneralMode.None ||
    drawLineAreaMode !== GeneralMode.None
  ) {
    if (isDrawLineNode(node)) {
      return true
    }
    if (isDrawLineAreaNode(node)) {
      return drawLineAreaMode !== GeneralMode.None
    }
    // 编辑连线区域模式下，不允许显示其他节点
    if (drawLineAreaMode === GeneralMode.Editable) {
      return false
    }
    // 在非 None 模式下，只显示连线区域
    return true
  }
  return true
}

export const Color = (): string => {
  const r = Math.floor(Math.random() * 255)
  const g = Math.floor(Math.random() * 255)
  const b = Math.floor(Math.random() * 255)
  return 'rgba(' + r + ',' + g + ',' + b + ', 1)'
}
/**
 * 所有虚拟节点的默认传参
 */
export const nodeConfig: Konva.RectConfig = {
  fill: 'transparent',
  draggable: true,
  strokeWidth: 0, // 在 hover 的场景中需要设置 stroke 的值，所以需要设置 strokeWidth
  stroke: 'transparent',
  name: SpecName,
  syncStyle: true, // 自定义属性，当为 false 时，不自动同步样式到 qc.Node 中
  dragLinkage: false, // 拖拽联动，如果有值，则是需要拖拽联动的 uuid 值
}

/**
 * 虚拟节点的默认传参, 根据不同节点联动
 * @param node
 */
export const initNodeConfig = (node: qc.Node): Konva.RectConfig => {
  const lockedNodeUuids = store.state.workspace.game.lockedNodeUuids
  const drawLineMode = store.state.workspace.stage.mode.drawLine
  const drawLineAreaMode = store.state.workspace.stage.mode.drawLineArea
  // 如果是锁定元素，或者是隐藏节点，则不显示并且不允许拖动，并且不需要绑定事件
  const lockStatus = lockedNodeUuids.some((id: string) => id === node.uuid)
  // 调试代码
  const debug = false
  return {
    ...nodeConfig,
    id: node.uuid,
    fill: (() => {
      let color
      switch (true) {
        case Boolean(debug):
          color = Color()
          break
        case isDrawLineNode(node):
          if (drawLineMode === GeneralMode.None) {
            color = 'transparent'
          } else {
            color = 'rgba(82, 196, 26, 0.3)'
          }
          break
        case isDrawLineAreaNode(node):
          color = 'rgba(243, 173, 2, 0.6)'
          break
        default:
          color = 'transparent'
          break
      }
      return color
    })(),
    draggable: (() => {
      return !(disableDraggable(node) || !node.worldVisible || lockStatus)
    })(),
    visible: (() => {
      if (isIvHotAreaNode(node) || isIvEndingInstallNode(node)) {
        return node.worldVisible
      }
      return !(
        carouselDisabledSelected(node) ||
        !node.worldVisible ||
        lockStatus ||
        StageData.activationShape.some(
          (shape) => shape.attrs.id === node.uuid,
        ) ||
        (drawLineAreaMode === GeneralMode.None && isDrawLineAreaNode(node))
      )
    })(),
    strokeWidth: (() => {
      if (isNodeGhost(node)) {
        return 1
      }
      return 0
    })(),
    stroke: (() => {
      if (isNodeGhost(node)) {
        return 'rgba(0,0,0,0.1)'
      }
      return 'transparent'
    })(),
    dragBoundFunc: (pos: Konva.Vector2d): Konva.Vector2d => {
      const activeNodeList = store.state.workspace.game.activeNodeList
      // 多选起止区域移动有问题，暂时无解，松手时挪到范围内做兜底补偿处理
      if (isRestrictedNode(node) && activeNodeList.length === 1) {
        const parentRect = StageData.layer!.findOne(`#${node.parent.uuid}`)!
        const parentWidth = parentRect.getClientRect().width
        const parentHeight = parentRect.getClientRect().height
        const parentX = parentRect.getClientRect().x
        const parentY = parentRect.getClientRect().y
        const targetRect = StageData.layer!.findOne(`#${node.uuid}`)!
        const targetWidth = targetRect.getClientRect().width
        const targetHeight = targetRect.getClientRect().height
        const targetX = targetRect.getClientRect().x
        const targetY = targetRect.getClientRect().y
        const transformer = StageData.transformer!
        const transformerWidth = transformer.width()
        const transformerHeight = transformer.height()
        const transformerX = transformer.x()
        const transformerY = transformer.y()
        const transformerAndTargetXGap = targetX - transformerX
        const transformerAndTargetYGap = targetY - transformerY
        const minX =
          parentX + transformerAndTargetYGap + targetWidth * node.pivotX
        const minY =
          parentY + transformerAndTargetYGap + targetHeight * node.pivotY
        const maxX =
          parentX +
          parentWidth -
          transformerWidth +
          transformerAndTargetXGap +
          targetWidth * node.pivotX
        const maxY =
          parentY +
          parentHeight -
          transformerHeight +
          transformerAndTargetYGap +
          targetHeight * node.pivotY
        return {
          x: Math.min(Math.max(pos.x, minX), maxX),
          y: Math.min(Math.max(pos.y, minY), maxY),
        }
      }
      return {
        x: pos.x,
        y: pos.y,
      }
    },
    dragLinkage: (() => {
      const editorData = editorScript.getScript(node)
      if (
        editorData &&
        editorData.$data &&
        editorData.$data.dragLinkageNodeUUID
      ) {
        return editorData.$data.dragLinkageNodeUUID
      }
      return false
    })(),
    textConfig: (() => {
      if (drawLineAreaMode !== GeneralMode.None && isDrawLineAreaNode(node)) {
        return {
          id: node.uuid + '_text',
          text: node.name,
          name: SpecSuffixName,
          fontSize: 12,
          fontFamily:
            'Helvetica Neue, Helvetica, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Arial, sans-serif',
          fill: 'white', // 白色字体颜色
          align: 'center', // 文本居中对齐
          verticalAlign: 'middle', // 文本垂直居中对齐
          width: 15,
          height: 15,
          offsetX: -2.5,
          offsetY: -2.5,
        }
      }
      if (isIvHotAreaNode(node) || isIvEndingInstallNode(node)) {
        return {
          id: node.uuid + '_text',
          text: getBrainItemActionIndexByNodeUuid(node.uuid),
          name: SpecSuffixName,
          fontSize: 12,
          fontFamily:
            'Helvetica Neue, Helvetica, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Arial, sans-serif',
          fill: 'white', // 白色字体颜色
          align: 'center', // 文本居中对齐
          verticalAlign: 'middle', // 文本垂直居中对齐
          width: 15,
          height: 15,
          offsetX: -2.5,
          offsetY: -2.5,
        }
      }
      return false
    })(),
    suffixConfig: (() => {
      if (
        (drawLineAreaMode !== GeneralMode.None && isDrawLineAreaNode(node)) ||
        isIvHotAreaNode(node) ||
        isIvEndingInstallNode(node)
      ) {
        return {
          id: node.uuid + '_suffix',
          name: SpecSuffixName,
          fill: 'black', // 白色字体颜色
          width: 15,
          height: 15,
          offsetX: -10,
          offsetY: -10,
          radius: 7.5,
        }
      }
      return false
    })(),
  }
}

export const isVirtualNode = (shape: Konva.Node): boolean => {
  return shape.name() === SpecName
}

export const isVirtualNodeGhost = (shape: Konva.Node): boolean => {
  const node = getNodeByShape(shape as Konva.Rect)
  return isNodeGhost(node)
}

export const setNodeHoverClass = (
  shape: Konva.Rect,
  hoverStatus: boolean,
): void => {
  const node = getNodeByShape(shape)
  // 避免元素的 scaleX/Y 过大，导致 storkeWidth 异常宽
  const strokeUnit = 1 / Math.max(shape.scaleX(), shape.scaleY())
  if (hoverStatus) {
    shape.stroke('#FAC415')
    shape.strokeWidth(strokeUnit)
  } else {
    if (isNodeGhost(node)) {
      shape.stroke('rgba(0,0,0,0.1)')
      shape.strokeWidth(strokeUnit)
    } else {
      shape.stroke('transparent')
      shape.strokeWidth(0)
    }
  }
}

export const isTransformer = (node: Konva.Node): boolean => {
  return node.className === 'Transformer'
}

export const selectionRectangleConfig: Konva.RectConfig = {
  fill: 'rgba(0,0,255,0.5)',
  visible: false,
}

// 轮播组件禁止选中子节点
function carouselDisabledSelected(node: qc.Node): boolean {
  if (isCarouselNodeChild(node)) {
    // 轮播组件禁止选中节点前缀
    const allParents = [...(getAllParentsBeforeScene(node) || []), node]
    const ignoreList = [isCarouselResourceGroupNode, isCarouselPiteGroupNode]
    if (ignoreList.some((val) => allParents.some((el) => val(el)))) {
      return true
    }
  }
  return false
}

export const disableBeSelected = (node: qc.Node): boolean => {
  return (
    isAudio(node) ||
    (isTempNode(node) && !isNodeGhost(node)) ||
    isLangImage(node) ||
    hasLayoutComponentParent(node) ||
    // carouselDisabledSelected(node) ||
    !allowShowNodeInDrawLineMode(node) ||
    (isIvPage() && (isGroup(node) || isIvGroup(node)))
  )
}

export const allowBeSelected = (node: qc.Node): boolean => {
  // 之前是白名单，现在改成黑名单，默认所有节点允许被选中
  return !disableBeSelected(node)
}

// 多选与 video 节点不允许旋转
export const allowBeRotation = (nodes: qc.Node[]): boolean => {
  return !(
    nodes.length > 1 ||
    isVideo(nodes[0]!) ||
    nodes.some((node) => isNodeGhost(node)) ||
    isDrawLineNode(nodes[0]!) ||
    isDrawLineAreaNode(nodes[0]!)
  )
}

// 如果当前节点的 pivot 非 0.5 也不允许缩放操作
export const allowBeResize = (nodes: qc.Node[]): boolean => {
  return !nodes.some((node) => isNodeGhost(node))
}

// 如果包含视频节点，则只能保持尺寸调整 或者 只要有1个锁，就按照等比率缩放
export const isKeepRatio = (nodes: qc.Node[]): boolean => {
  return nodes.some((node) => {
    const ped = editorScript.getScript(node)
    return isVideo(node) || ped?.$data.ratioLock
  })
}

// 判断当前节点是否禁止编辑
export const isBanEditNode = (node: qc.Node): boolean => {
  const lockedNodeUuids = store.state.workspace.game.lockedNodeUuids
  const drawLineMode = store.state.workspace.stage.mode.drawLine
  const drawLineAreaMode = store.state.workspace.stage.mode.drawLineArea
  // 如果包含锁定元素，则不允许拖拽与缩放
  let nodeId = node.uuid
  // 如果是连线区域，则受父级影响
  if (isDrawLineAreaNode(node)) {
    nodeId = node.parent.uuid
  }
  const lockOrHidden = lockedNodeUuids.some((id: string) => id === nodeId)
  const editDragArea = store.state.workspace.game.availableNodeWhenLockEdit
  const lockEditAll = store.state.workspace.game.lockEditAll
  const isEditingDragArea = editDragArea.some(
    (avaNode: qc.Node) => avaNode.uuid === node.uuid,
  )
  const qcNode = nodePool.find(node.uuid)
  if (isDragTargetArea(qcNode) || isDragBound(qcNode)) {
    if (lockEditAll) {
      return !isEditingDragArea
    }
    return true
  }
  // 有跟手图片的笔触禁止在画布中操作
  if (isEraseBrushCategory(qcNode.parent) && hasBrushImages(qcNode)) {
    return true
  }
  // 布局组件内的节点禁止画布中操作
  if (hasLayoutComponentParent(qcNode)) {
    return true
  }
  // 遮罩组件里的跟随元素组
  if (isFollowElementNode(qcNode)) {
    return true
  }
  if (lockOrHidden || lockEditAll) {
    return true
  }
  if (isDrawLineAreaNode(node)) {
    return drawLineAreaMode !== GeneralMode.Editable
  }
  if (isIvPage() && (isGroup(qcNode) || isIvGroup(qcNode))) {
    return true
  }
  return false
}

// 判断当前 shape 是否有被选中
export const isSelectedShape = (shape: Konva.Rect): boolean => {
  const selectShapes = StageData.transformer?.nodes()
  if (selectShapes && selectShapes.length) {
    return selectShapes.some((item) => item === shape)
  }
  return false
}

// 判断当前节点能否被选中
export const isBanSelectNode = (shape: Konva.Rect): boolean => {
  const uuid = shape.id()
  const qcNode = nodePool.find(uuid)
  const lockEditAll = store.state.workspace.game.lockEditAll
  const editDragArea = store.state.workspace.game.availableNodeWhenLockEdit
  const isEditingDragArea = editDragArea.some(
    (avaNode: qc.Node) => avaNode.uuid === uuid,
  )
  if ((isDragTargetArea(qcNode) || isDragBound(qcNode)) && !lockEditAll) {
    return !isEditingDragArea
  }
  // 有跟手图片的笔触禁止在画布中操作
  if (isEraseBrushCategory(qcNode.parent) && hasBrushImages(qcNode)) {
    return true
  }
  // 布局组件内的节点禁止画布中操作
  if (hasLayoutComponentParent(qcNode)) {
    return true
  }
  // 遮罩组件里的跟随元素组
  if (isFollowElementNode(qcNode)) {
    return true
  }
  if (isIvPage() && (isGroup(qcNode) || isIvGroup(qcNode))) {
    return true
  }
  return false
}

// 是否允许框选
export const isAllowBoxSelection = (shape: Konva.Rect): boolean => {
  const uuid = shape.id()
  const qcNode = nodePool.find(uuid)
  return getNodeLvl(qcNode) === 1
}
